import React, { useMemo, useState } from 'react';

const doshas = [
  {
    name: 'Vata',
    qualities: 'Light, dry, cool, and mobile.',
    yogaPoses: [
      'Child\'s Pose (Balasana)',
      'Cat-Cow (Marjaryasana-Bitilasana)',
      'Seated Forward Fold (Paschimottanasana)',
      'Legs-Up-The-Wall (Viparita Karani)',
    ],
    diet: [
      'Warm, cooked meals with healthy oils',
      'Soups and stews with grounding vegetables',
      'Sweet, sour, and salty flavors',
      'Herbal teas like ginger or cinnamon',
    ],
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <path
          d="M12 36c12-2 20-10 28-22 8 12 6 24-6 34-10 8-22 6-30-2 2-4 4-6 8-10z"
          fill="currentColor"
          opacity="0.2"
        />
        <path
          d="M18 42c10-2 18-9 24-20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="16" cy="46" r="4" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: 'Pitta',
    qualities: 'Hot, sharp, and intense.',
    yogaPoses: [
      'Moon Salutations (Chandra Namaskar)',
      'Seated Twist (Ardha Matsyendrasana)',
      'Bridge Pose (Setu Bandhasana)',
      'Wide-Legged Forward Fold (Prasarita Padottanasana)',
    ],
    diet: [
      'Cooling foods like cucumbers and leafy greens',
      'Sweet, bitter, and astringent flavors',
      'Coconut water or mint tea',
      'Limit spicy, fried, or acidic foods',
    ],
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <path
          d="M32 6c10 8 16 18 16 30 0 10-6 20-16 22-10-2-16-12-16-22 0-12 6-22 16-30z"
          fill="currentColor"
          opacity="0.18"
        />
        <path
          d="M32 16c6 6 8 12 8 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M32 26c-4 4-6 8-6 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    name: 'Kapha',
    qualities: 'Heavy, slow, and steady.',
    yogaPoses: [
      'Sun Salutations (Surya Namaskar)',
      'Chair Pose (Utkatasana)',
      'Warrior II (Virabhadrasana II)',
      'Cobra Pose (Bhujangasana)',
    ],
    diet: [
      'Light, warm meals with plenty of vegetables',
      'Pungent, bitter, and astringent flavors',
      'Spices like turmeric, black pepper, and ginger',
      'Reduce dairy and heavy, oily foods',
    ],
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <path
          d="M16 36c0-12 8-20 16-26 8 6 16 14 16 26 0 12-8 20-16 22-8-2-16-10-16-22z"
          fill="currentColor"
          opacity="0.18"
        />
        <path
          d="M32 20v26"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M26 28c2 3 4 6 6 10 2-4 4-7 6-10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const quizQuestions = [
  {
    id: 'build',
    question: 'Which body type fits you best?',
    options: [
      { label: 'Lean, light, or wiry', dosha: 'Vata' },
      { label: 'Athletic, medium build', dosha: 'Pitta' },
      { label: 'Solid, sturdy, or curvy', dosha: 'Kapha' },
    ],
  },
  {
    id: 'energy',
    question: 'How does your energy feel most days?',
    options: [
      { label: 'Up and down, changeable', dosha: 'Vata' },
      { label: 'Driven and intense', dosha: 'Pitta' },
      { label: 'Steady and calm', dosha: 'Kapha' },
    ],
  },
  {
    id: 'temperature',
    question: 'Which temperature feels most comfortable?',
    options: [
      { label: 'Warm and cozy', dosha: 'Vata' },
      { label: 'Cool and breezy', dosha: 'Pitta' },
      { label: 'Warm and dry', dosha: 'Kapha' },
    ],
  },
  {
    id: 'mind',
    question: 'How would you describe your mind?',
    options: [
      { label: 'Creative, quick, sometimes scattered', dosha: 'Vata' },
      { label: 'Focused, sharp, goal-oriented', dosha: 'Pitta' },
      { label: 'Patient, steady, supportive', dosha: 'Kapha' },
    ],
  },
  {
    id: 'appetite',
    question: 'What is your appetite like?',
    options: [
      { label: 'Irregular, sometimes light', dosha: 'Vata' },
      { label: 'Strong, easily hungry', dosha: 'Pitta' },
      { label: 'Steady, can skip meals', dosha: 'Kapha' },
    ],
  },
];

function DoshaWellness() {
  const [answers, setAnswers] = useState({});

  const scores = useMemo(() => {
    return Object.values(answers).reduce(
      (acc, dosha) => {
        acc[dosha] += 1;
        return acc;
      },
      { Vata: 0, Pitta: 0, Kapha: 0 }
    );
  }, [answers]);

  const topDosha = useMemo(() => {
    const entries = Object.entries(scores);
    const max = Math.max(...entries.map(([, value]) => value));
    const leaders = entries.filter(([, value]) => value === max).map(([key]) => key);
    if (max === 0) {
      return null;
    }
    return leaders.length > 1 ? 'Balanced' : leaders[0];
  }, [scores]);

  return (
    <section className="dosha">
      <div className="dosha__hero" role="img" aria-label="Herbs and leaves arranged on a calm beige background">
        <img src="./src/image/image.png" alt="" className="dosha__hero-image" />
        <div className="dosha__hero-overlay">
          <p className="dosha__hero-eyebrow">Ayuveda Balance</p>
          <h1 className="dosha__hero-title">Vedic Vital</h1>
        </div>
      </div>
      <header className="dosha__header">
        <p className="dosha__eyebrow">Ayurvedic Balance</p>
        <h1>Dosha Wellness Guide</h1>
        <p className="dosha__intro">
          Yoga poses and diet guidance tailored to each Ayurvedic dosha, designed
          to restore balance with daily, practical rituals.
        </p>
      </header>
      <div className="dosha__grid">
        {doshas.map((dosha) => (
          <article key={dosha.name} className="dosha-card">
            <div className="dosha-card__header">
              <h2>{dosha.name}</h2>
              <span className="dosha-card__tag">{dosha.qualities}</span>
            </div>
            <div className="dosha-card__icon">{dosha.icon}</div>
            <div className="dosha-card__section">
              <h3>Yoga Poses</h3>
              <ul className="dosha-card__list">
                {dosha.yogaPoses.map((pose) => (
                  <li key={pose}>{pose}</li>
                ))}
              </ul>
            </div>
            <div className="dosha-card__section">
              <h3>Diet Focus</h3>
              <ul className="dosha-card__list">
                {dosha.diet.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>
      <section className="dosha-quiz">
        <div className="dosha-quiz__header">
          <p className="dosha__eyebrow">Self Assessment</p>
          <h2>Discover Your Dominant Dosha</h2>
          <p className="dosha__intro">
            Answer a few quick questions to see which dosha is currently most
            prominent for you.
          </p>
        </div>
        <div className="dosha-quiz__content">
          <div className="dosha-quiz__questions">
            {quizQuestions.map((q) => (
              <div key={q.id} className="dosha-quiz__card">
                <h3>{q.question}</h3>
                <div className="dosha-quiz__options">
                  {q.options.map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      className={
                        answers[q.id] === option.dosha
                          ? 'dosha-quiz__option is-active'
                          : 'dosha-quiz__option'
                      }
                      onClick={() =>
                        setAnswers((prev) => ({ ...prev, [q.id]: option.dosha }))
                      }
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <aside className="dosha-quiz__result">
            <h3>Your Result</h3>
            <p className="dosha-quiz__result-label">
              {topDosha
                ? topDosha === 'Balanced'
                  ? 'Balanced Mix'
                  : topDosha
                : 'Answer the questions to see your result'}
            </p>
            <p className="dosha-quiz__result-detail">
              Vata: {scores.Vata} | Pitta: {scores.Pitta} | Kapha: {scores.Kapha}
            </p>
            <button
              type="button"
              className="dosha-quiz__reset"
              onClick={() => setAnswers({})}
            >
              Reset answers
            </button>
          </aside>
        </div>
      </section>
    </section>
  );
}

export default DoshaWellness;
